import { PlotData } from "plotly.js";
import { 
    ClosedRatioData,
    CumulativeDefectData,
    DefectInsightFilterBy,
    AgeOfOpenDefectsData,
    MostCommonDefectsData
} from "../types/DefectInsightsData";
import { groupBy, sum, uniq } from "lodash";
import { DefectDataItem } from "../types/DefectsDataItem";

const closedRatioColours = ["#B4CC47", "#E62788", "#FFA504", "#3495D2"];

const cumulativeDefectColours = {
    total: "#68658C",
    closed: "#53A440"
};

const ageOfOpenDefectPeriods = ['>1 year', '6-12 months', '3-6 months', '1-3 months', '<4 weeks'];

const highText = "<b style='color:#EF5A14'>HIGH:</b>";
const mediumText = "<b style='color:#E5940E'>MEDIUM:</b>";
const lowText = "<b style='color:#F8D311'>LOW:</b>";

const closedRatioPlotDefaults: Partial<PlotData> = {
    type: "scatter",
    mode: 'lines',
    hovertemplate: '%{x}: %{y}',
    line: { width: 3 },
}

const cumulativeDefectPlotDefaults: Partial<PlotData> = {
    type: "scatter",
    mode: "lines",
    line: { width: 3 }
}

const ageOpenPlotDefaults: Partial<PlotData> = {
    type: "bar",
    hovertemplate: '%{x}: %{y}',
}

const mostCommonPlotDefaults: Partial<PlotData> = {
    type: "bar",
    hovertemplate: '%{x}: %{y}',
    orientation: "h",
    textfont: {
        color: "white",
        size: 14
    },
    textposition: "inside",
    insidetextanchor: "middle"
}

export const mapClosedRatioToPlotData = (
    inputclosedRatioData: ClosedRatioData[] | undefined,
    filterBy: DefectInsightFilterBy,
    filterValues: string[] | undefined,
) => {
    if (inputclosedRatioData?.length) {
        // For prod now, remove later
        const closedRatioData = inputclosedRatioData.map((i) => ({...i, date: new Date(i.date).toISOString().split('T')[0]}));

        if (filterValues === undefined) {
            const closedRatiosByDate = groupBy(closedRatioData, 'date');

            const dates = Object.keys(closedRatiosByDate);
            const closedRatios = Object.values(closedRatiosByDate).map(calculateRatios);

            return [{
                ...closedRatioPlotDefaults,
                x: dates,
                y: closedRatios,
                marker: {
                    color: "#28235B",
                },
                name: "All",     
            }];
        }

        if (filterValues.length) {
            if (filterBy === DefectInsightFilterBy.TECHNICAL_MANAGERS) {
                const groupedClosedRatios = groupByTechnicalManager(closedRatioData);

                for (const technicalManager in groupedClosedRatios) {
                    const closedRatiosByTechnicalManager = groupedClosedRatios[technicalManager];

                    if (closedRatiosByTechnicalManager?.length) {
                        const groupedByDate = groupBy(closedRatiosByTechnicalManager, "date");

                        const flattenedRatios = Object.values((groupedByDate)).map((byDate) => ({
                            ...byDate[0],
                            ratio: calculateRatios(byDate)
                        }));

                        groupedClosedRatios[technicalManager] = flattenedRatios;
                    }
                }


                return mapGroupedClosedRatios(groupedClosedRatios);
            }

            const closedRatiosByVessels = groupByVessel(closedRatioData);

            return mapGroupedClosedRatios(closedRatiosByVessels);
        }
    }

    return [];
}

export const mapCumulativeDefectToPlotData = (
    inputcumulativeDefectData: CumulativeDefectData[] | undefined,
    filterBy: DefectInsightFilterBy,
    filterValues: string[] | undefined,
) => {
    if (inputcumulativeDefectData?.length) {
        // For prod now, remove later
        let cumulativeDefectData = inputcumulativeDefectData.map((i) => ({...i, date: new Date(i.date).toISOString().split('T')[0]}));

        if (filterValues !== undefined) {
            if (filterBy === DefectInsightFilterBy.TECHNICAL_MANAGERS) {
                cumulativeDefectData = cumulativeDefectData.filter((cumulativeDefect) =>
                    filterValues.includes(cumulativeDefect.technical_manager)
                );
            }
            else {
                cumulativeDefectData = cumulativeDefectData.filter((cumulativeDefect) =>
                    filterValues.includes(cumulativeDefect.vessel_name) ||
                    filterValues.includes(cumulativeDefect.vessel) ||
                    filterValues.some(filterValue => 
                        cumulativeDefect.vessel_name === filterValue ||
                        cumulativeDefect.vessel === filterValue ||
                        cumulativeDefect.vessel_name.includes(filterValue) ||
                        cumulativeDefect.vessel.includes(filterValue)
                    )
                );
            }
        }
        
        if (cumulativeDefectData.length === 0) {
            return [];
        }
        
        const cumulativeDefectByDate = groupBy(cumulativeDefectData, "date");
        const dates = Object.keys(cumulativeDefectByDate);

        return mapCumulativeDefects(Object.values(cumulativeDefectByDate), dates);
    }
    return [];
}

export const mapAgeOfOpenDefectsToPlotData = (
    ageOfOpenDefectsData: AgeOfOpenDefectsData[] | undefined,
    filterBy: DefectInsightFilterBy,
    filterValues: string[] | undefined,
) => {
    if (ageOfOpenDefectsData?.length) {
        if (filterValues === undefined) {

            const periods = groupBy(ageOfOpenDefectsData, 'period');
            const periodTotals = calculatePeriodTotals(periods);

            return [{
                ...ageOpenPlotDefaults,
                x: Object.keys(periods).map((period) => ageOfOpenDefectPeriods[Number(period)]),
                y: periodTotals.map((period) => period.totalSum),
                marker: {
                    color: "#28235B",
                },
                name: "All",
                width: 0.4,
                hovertemplate: periodTotals.map((period) => `${highText} ${period.highSum}<br>${mediumText} ${period.mediumSum}<br>${lowText} ${period.lowSum}<br>TOTAL: ${period.totalSum}`),
            }];
        }

        if (filterValues.length) {
            if (filterBy === DefectInsightFilterBy.TECHNICAL_MANAGERS) {
                const groupedAgeOfOpenDefects = groupByTechnicalManager(ageOfOpenDefectsData);

                for (const technicalManager in groupedAgeOfOpenDefects) {
                    const ageOfOpenDefectsByTechnicalManager = groupedAgeOfOpenDefects[technicalManager];

                    if (ageOfOpenDefectsByTechnicalManager?.length) {
                        const groupedByDate = groupBy(ageOfOpenDefectsByTechnicalManager, "date");

                        const flattenedTotals = Object.values((groupedByDate)).map((byDate) => ({
                            ...byDate[0],
                            total: sum(byDate.map((item) => item.total))
                        }));

                        groupedAgeOfOpenDefects[technicalManager] = flattenedTotals;
                    }
                }

                return mapGroupedAgeOfOpenDefects(groupedAgeOfOpenDefects);
            }
            const ageOfOpenDefectsByVessels = groupByVessel(ageOfOpenDefectsData);

            return mapGroupedAgeOfOpenDefects(ageOfOpenDefectsByVessels);
        }
    }

    return [];
}

const groupByVessel = <T>(data: T[]) => groupBy(data, "vessel_name");

const groupByTechnicalManager = <T>(data: T[]) => groupBy(data, "technical_manager");

const mapGroupedClosedRatios = (groupedClosedRatios: Record<string, ClosedRatioData[]>) => {
    const closedRatioEntries = Object.entries(groupedClosedRatios);

    const mappedClosedRatios = closedRatioEntries.map(([vessel, closedRatios], index) => ({
        ...closedRatioPlotDefaults,
        x: closedRatios.map((closedRatio) => closedRatio.date),
        y: closedRatios.map((closedRatio) => closedRatio.ratio),
        marker: {
            color: closedRatioColours[index],
        },
        name: addLinebreaksToName(vessel),
    }));

    return mappedClosedRatios;
};

const calculateRatios = (closedRatioData: ClosedRatioData[]) => {
    let closedSum = 0;
    let openSum = 0;

    closedRatioData.forEach((closedRatio) => {
        closedSum += closedRatio.closed;
        openSum += closedRatio.open;
    })

    return Math.floor((closedSum /  openSum) * 100);
};

const mapCumulativeDefects = (cumulativeDefectData: CumulativeDefectData[][], dates: string[]) => {
    const closed: number[] = [];
    const total: number[] = [];


    cumulativeDefectData.forEach((cumulativeDefects) => {
        let closedSum =  0;
        let totalSum =   0;

        cumulativeDefects.forEach((cumulativeDefect) => {
            closedSum += cumulativeDefect.closed;
            totalSum += cumulativeDefect.open + cumulativeDefect.closed;
        });
        
        closed.push(closedSum);
        total.push(totalSum);
    })

    return [
        {
            ...cumulativeDefectPlotDefaults,
            hovertemplate: "Closed: %{y}",
            x: dates,
            y: closed,
            marker: {
                color: cumulativeDefectColours.closed
            },
            name: "Closed"
        },
        {
            ...cumulativeDefectPlotDefaults,
            hovertemplate: "Total: %{y}",
            x: dates,
            y: total,
            marker: {
                color: cumulativeDefectColours.total
            },
            name: "Total"
        },
    ];    
}

const addLinebreaksToName = (name: string) => name.replace(/(^[\w\s,]{13,}?)\s?\b/g, "$1<br>")

const calculatePeriodTotals = (periods: any) => {
    const periodTotals = [];
    
    for (const period in periods) {
        const highSum = periods[period].reduce((acc: any, current: any) => acc + current.high, 0);
        const mediumSum = periods[period].reduce((acc: any, current: any) => acc + current.medium, 0);
        const lowSum = periods[period].reduce((acc: any, current: any) => acc + current.low, 0);
        const totalSum = periods[period].reduce((acc: any, current: any) => acc + current.total, 0);
   
        periodTotals.push({highSum: highSum, mediumSum: mediumSum, lowSum: lowSum, totalSum: totalSum});
    }
    
    return periodTotals;
}

const mapGroupedAgeOfOpenDefects = (groupedAgeOfOpenDefects: Record<string, AgeOfOpenDefectsData[]>) => {
    const ageOfOpenDefectsEntries = Object.entries(groupedAgeOfOpenDefects);

    const mappedAgeOfOpenDefectEntries = ageOfOpenDefectsEntries.map(([vessel, ageOfOpenDefects], index) => ({
        ...ageOpenPlotDefaults,
        x: ageOfOpenDefects.map((ageOfOpenDefects) => ageOfOpenDefectPeriods[ageOfOpenDefects.period]),
        y: ageOfOpenDefects.map((ageOfOpenDefect) => ageOfOpenDefect.total),
        marker: {
            color: closedRatioColours[index],
        },
        hovertemplate: ageOfOpenDefects.map((ageOfOpenDefects) => 
            `${highText} ${ageOfOpenDefects.high}<br>${mediumText} ${ageOfOpenDefects.medium}<br>${lowText} ${ageOfOpenDefects.low}<br>TOTAL: ${ageOfOpenDefects.total}`),
        name: addLinebreaksToName(vessel),
        width: 0.4
    }));

    return mappedAgeOfOpenDefectEntries;
};

export const getFilterValues = (defectData: DefectDataItem[], filterBy: DefectInsightFilterBy, selectedVessel: string) => {
    let filterValues = [];

    if (filterBy === DefectInsightFilterBy.VESSELS) {
        if (selectedVessel) {
            filterValues = [selectedVessel];
        } else {
            filterValues = defectData.map((defect) => defect.vesselName);
        }
    } else if (selectedVessel) {
            filterValues = defectData.reduce((managers: string[], defect) => {
                if (defect.vesselName === selectedVessel) {
                    managers.push(defect.technicalManager);
                }

                return managers;
            }, []);
    } else {
        filterValues = defectData.map((defect) => defect.technicalManager.trim())
    }

    return uniq(filterValues);
}

const sortAndSliceMostCommonDefects = (
    mostCommonDefectsData: MostCommonDefectsData[]
) => {
    const commonDefectsByType = groupBy(mostCommonDefectsData, "type_code");

    const commonDefects = Object.values(commonDefectsByType).filter(
        (defects) => defects.length > 1
    );
    commonDefects.sort((a, b) => b.length - a.length);

    return commonDefects.slice(0, 10);
};

const mapMostCommonDefects = (
    mostCommonDefectsData: MostCommonDefectsData[][],
    key?: string,
    index?: number
) => {
    const xValues: number[] = [];
    const yValues: string[] = [];
    const hoverValues: string[] = [];
    const typeCodeValues: string[] = [];

    mostCommonDefectsData.forEach((defects) => {
        const comment = defects[0].display_name ?? defects[0].short_gen_name;
        const displayName = defects[0].display_name ?? defects[0].type_code;
        typeCodeValues.push(defects[0].type_code);
        xValues.push(defects.length);
        yValues.push(displayName?.length > 40 ? displayName.substring(0, 40) + '...' : displayName);
        hoverValues.push(
            `<b>${comment}</b><br>Area: ${defects[0].category}<br>Code: ${defects[0].type_code}<br>—<br>Defects: %{x}`
        );
    });

    const mappedMostCommonDefects = {
        ...mostCommonPlotDefaults,
        x: xValues,
        y: yValues,
        customdata: typeCodeValues,
        marker: {
            color: index == undefined ? "#28235B" : closedRatioColours[index]
        },
        name: key ?? "All",
        text: xValues.map(String),
        hovertemplate: hoverValues
    };

    return mappedMostCommonDefects;
};

const mapSortedMostCommonDefects = (
    sortedDefects: MostCommonDefectsData[][],
    key: string
) => {
    try {
        const groupedByKey: Record<string, Record<string, MostCommonDefectsData[]>> = {};

        sortedDefects.forEach((defects) => {
            defects.forEach((defect) => {
                const groupKey = (defect as any)[key];
                const type_code = defect.type_code;

                if (!groupedByKey[groupKey]) {
                    groupedByKey[groupKey] = {
                        [type_code]: [defect]
                    };
                } else if (!groupedByKey[groupKey][type_code]) {
                    groupedByKey[groupKey] = {
                        ...groupedByKey[groupKey],
                        [type_code]: [defect]
                    };
                } else {
                    groupedByKey[groupKey][type_code].push(defect);
                }
            })
        });

        return Object.entries(groupedByKey).map(([groupedKey, defects], index) =>
            mapMostCommonDefects(Object.values(defects), groupedKey, index)
        );
    } catch (e) {
        console.log(e);
    }

    return [];
};

export const mapMostCommonDefectsData = (
    mostCommonDefectsData: MostCommonDefectsData[],
    filterBy: DefectInsightFilterBy,
    filterValues: string[] | undefined
) => {
    if (mostCommonDefectsData?.length) {
        const sortedAndSliced = sortAndSliceMostCommonDefects(
            mostCommonDefectsData
        );

        if (filterValues === undefined) {
            return [mapMostCommonDefects(sortedAndSliced)];
        }

        if (filterValues.length) {
            return mapSortedMostCommonDefects(
                sortedAndSliced,
                filterBy === DefectInsightFilterBy.TECHNICAL_MANAGERS
                    ? "technical_manager"
                    : "vessel_name"
            );
        }
    }

    return [];
};
